import axios, { AxiosResponse } from '../../node_modules/axios/index';
import { getApiUrl } from '../constants/api';
import { Product } from '../types/product';
import { isFeatureEnabled } from '../utils/analytics';
import { handleCurrencyChange, getTaxWithRetry } from '../utils/calculate-tax';

export interface RecommendationsUIStyles {
  productHeight?: string;
  productWidth?: string;
  productsSpacing?: string;
  productPriceTextColor?: string;
  productAddToCartButtonColor?: string;
  productAddToCartButtonTextColor?: string;
  productAddToCartButtonText?: string;
  productBorderRadius?: string;
  productAddToCartButtonBorderRadius?: string;
  productAddToCartButtonType?: 'filled' | 'contained';
  enableEnglishPrices?: boolean;
}

export interface Recommendation {
  id: number;
  createdAt: string;
  updatedAt: string;
  pageType: 'HOME_PAGE' | 'PRODUCT_DETAILS_PAGE' | 'CART_PAGE';
  type: string;
  title: string;
  titleEn: string;
  titlePosition: 'CENTERED' | 'RIGHT';
  numberOfProducts: number;
  isActive: boolean;
  storeId: number;
  uiStyles: RecommendationsUIStyles;
  elementSelector?: string;
}

const DEFAULT_UI_STYLES: RecommendationsUIStyles = {
  productHeight: '23rem',
  productWidth: '14rem',
  productsSpacing: '1rem',
  productPriceTextColor: '#363636',
  productAddToCartButtonColor: '#aed8e0',
  productAddToCartButtonTextColor: '#aed8e0',
  productAddToCartButtonText: 'إضافة للسلة',
  productBorderRadius: '0.75rem',
  productAddToCartButtonBorderRadius: '0.75rem',
  productAddToCartButtonType: 'filled',
  enableEnglishPrices: false,
};

export interface GetActiveRecommendationsWithProductsParams {
  storeId: string;
  storeType: string;
  pageType: string;
  customerId: string;
  productId?: string;
  currency?: string;
}

export interface ActiveRecommendationWithProducts {
  products: Product[];
  recommendation: Recommendation;
}

interface ActiveRecommendationsResponse {
  meta: any;
  results: ActiveRecommendationWithProducts[];
}

function applyDefaultUIStyles(
  uiStyles: RecommendationsUIStyles,
): RecommendationsUIStyles {
  return {
    productAddToCartButtonBorderRadius:
      uiStyles.productAddToCartButtonBorderRadius ||
      DEFAULT_UI_STYLES.productAddToCartButtonBorderRadius,
    productAddToCartButtonColor:
      uiStyles.productAddToCartButtonColor ||
      DEFAULT_UI_STYLES.productAddToCartButtonColor,
    productAddToCartButtonText:
      uiStyles.productAddToCartButtonText ||
      DEFAULT_UI_STYLES.productAddToCartButtonText,
    productHeight: uiStyles.productHeight || DEFAULT_UI_STYLES.productHeight,
    productWidth: uiStyles.productWidth || DEFAULT_UI_STYLES.productWidth,
    productsSpacing:
      uiStyles.productsSpacing || DEFAULT_UI_STYLES.productsSpacing,
    productAddToCartButtonTextColor:
      uiStyles.productAddToCartButtonTextColor ||
      DEFAULT_UI_STYLES.productAddToCartButtonTextColor,
    productAddToCartButtonType:
      uiStyles.productAddToCartButtonType ||
      DEFAULT_UI_STYLES.productAddToCartButtonType,
    productBorderRadius:
      uiStyles.productBorderRadius || DEFAULT_UI_STYLES.productBorderRadius,
    productPriceTextColor:
      uiStyles.productPriceTextColor || DEFAULT_UI_STYLES.productPriceTextColor,
    enableEnglishPrices:
      uiStyles.enableEnglishPrices || DEFAULT_UI_STYLES.enableEnglishPrices,
  };
}

export async function getActiveRecommendationsWithProducts({
  customerId,
  storeId,
  pageType,
  storeType,
  productId,
  currency,
}: GetActiveRecommendationsWithProductsParams): Promise<
  ActiveRecommendationWithProducts[]
> {
  const sessionId = localStorage.getItem('alfinder_session_id');
  const recentlyViewedProducts = localStorage.getItem(
    'alfinder_recently_viewed',
  )
    ? JSON.parse(localStorage.getItem('alfinder_recently_viewed') || '')
    : undefined;
  const headers =
    isFeatureEnabled('alfinder_customer_user') === true
      ? {
          'X-Alfinder-Session-Id': sessionId || '',
        }
      : {};
  console.log('[getRecommendations]', { sessionId, headers });

  const response: AxiosResponse<ActiveRecommendationsResponse> =
    await axios.get(
      `${getApiUrl(storeId)}/stores/${storeId}/recommendations/products/active`,
      {
        params: {
          customerId,
          pageType,
          storeType,
          productId: productId ? productId : undefined,
          productIds: recentlyViewedProducts
            ? recentlyViewedProducts.join(',')
            : undefined,
        },
        headers,
      },
    );

  try {
    if (response.data.results.length > 0) {
      const currentCurrency = currency;
      handleCurrencyChange(currentCurrency as string);

      // Get tax with retry logic
      const tax = await getTaxWithRetry(
        storeType,
        response.data.results[0].products[0].productId,
        response.data.results[0].products[0].price,
      );

      // If we couldn't get a valid tax after retries, return original data
      if (tax === null || isNaN(tax) || !isFinite(tax) || tax <= 0) {
        console.warn(
          'Could not calculate valid tax for recommendations, returning original prices',
        );
        return response.data.results;
      }

      const manipulatedRecommendations = response.data.results.map(
        (recommendationWithProducts) => {
          return {
            recommendation: {
              ...recommendationWithProducts.recommendation,
              uiStyles: applyDefaultUIStyles(
                recommendationWithProducts.recommendation.uiStyles,
              ),
            },
            products: recommendationWithProducts.products.map((product) => {
              return {
                ...product,
                price: product.isTaxable ? product.price * tax : product.price,
                discountedPrice: product?.discountedPrice
                  ? product.isTaxable
                    ? product.discountedPrice * tax
                    : product.discountedPrice
                  : undefined,
              };
            }),
          };
        },
      );

      return manipulatedRecommendations;
    }

    return response.data.results;
  } catch (error) {
    console.error('Error processing product prices:', error);
    return response.data.results;
  }
}
